/**
 * MessagingAuditPage — 149-cb. BOD Lv1 only. Read-only audit isi percakapan
 * pribadi antar user. Selalu meminta alasan (min 8 karakter). Tampilan sengaja
 * DIBEDAKAN dari MessengerDrawer (banner merah, badge AUDIT ACCESS) supaya
 * tidak terlihat seperti percakapan pribadi biasa.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Search, Clock, User, FileText, Loader2 } from "lucide-react";
import { Navigate } from "react-router-dom";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import StickyContextBar from "@/components/StickyContextBar";

export default function MessagingAuditPage() {
  const { user } = useAuth();
  const isBOD = user?.division === "BOD" && Number(user?.level) === 1;

  const [logs, setLogs] = useState([]);
  const [convs, setConvs] = useState([]);
  const [users, setUsers] = useState([]);
  const [inputA, setInputA] = useState("");
  const [inputB, setInputB] = useState("");
  const [reason, setReason] = useState("");
  const [inspecting, setInspecting] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isBOD) return;
    api.get("/messaging/admin/audit-log").then(({ data }) => setLogs(data || [])).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, [isBOD]);

  if (!user) return null;
  if (!isBOD) return <Navigate to="/dashboard" replace />;

  const request = async () => {
    if (!inputA || !inputB || inputA === inputB) { toast.error("Pilih dua user yang berbeda"); return; }
    if (reason.trim().length < 8) { toast.error("Alasan wajib minimal 8 karakter"); return; }
    setBusy(true);
    try {
      // find or open conv by pair (this requires backend to look up by pair — open endpoint
      // returns existing conv, but audit endpoint expects conversation_id. So auditor opens
      // a *virtual* lookup: call the open endpoint pretending to be the auditor is not right.
      // Instead, backend audit-access accepts a conversation_id. We compute via search: BOD
      // simply pastes conversation_id, OR we resolve pair via a helper.
      // Simpler flow: use /conversations to list — but auditor is not a participant.
      // We'll add explicit lookup helper: read from server via query params.
      const { data: pair } = await api.get(`/messaging/admin/find-pair?a=${inputA}&b=${inputB}`);
      if (!pair.id) { toast.error("Belum ada percakapan antara dua user tsb"); setBusy(false); return; }
      const { data } = await api.post("/messaging/admin/audit-access", { conversation_id: pair.id, reason: reason.trim() });
      setInspecting(data);
      setReason("");
      const { data: log2 } = await api.get("/messaging/admin/audit-log");
      setLogs(log2 || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal audit"); }
    finally { setBusy(false); }
  };

  const nameOf = (uid) => users.find((u) => u.id === uid)?.name || uid.slice(0, 8);

  return (
    <div className="p-6 space-y-4" data-testid="messaging-audit-page">
      <div className="border-l-4 border-rose-600 bg-rose-50 px-4 py-3 flex items-start gap-3">
        <ShieldAlert size={20} className="text-rose-700 shrink-0 mt-0.5" />
        <div>
          <div className="font-display text-lg text-rose-900">Audit Isi Percakapan — Akses Sensitif</div>
          <p className="text-xs text-rose-800 mt-0.5">Halaman ini <b>bukan</b> percakapan biasa. Semua akses dicatat dengan alasan, auditor, dan target. Digunakan hanya untuk kepatuhan / investigasi resmi.</p>
        </div>
      </div>

      <StickyContextBar segments={["Kontrol & Audit", "Audit Pesan", `${logs.length} akses tercatat`]} />

      <section className="bg-white border border-slate-200 p-4 space-y-3" data-testid="audit-request">
        <h3 className="text-sm font-semibold text-[#0B1D3A]">Ajukan Akses Baru</h3>
        <div className="grid grid-cols-2 gap-3">
          <UserPickField label="User A" value={inputA} onChange={setInputA} users={users} testid="audit-user-a" />
          <UserPickField label="User B" value={inputB} onChange={setInputB} users={users} testid="audit-user-b" />
        </div>
        <div>
          <label className="overline text-slate-600 text-[10px]">Alasan Audit *</label>
          <textarea data-testid="audit-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            placeholder="Contoh: Investigasi laporan pelanggaran policy komunikasi tanggal 21 Feb 2026"
            className="w-full border border-slate-300 px-2 py-1.5 text-xs mt-0.5" />
          <div className="text-[10px] text-slate-500 mt-0.5">Minimal 8 karakter. Alasan disimpan permanen bersama waktu, auditor, dan target.</div>
        </div>
        <Button data-testid="audit-submit" onClick={request} disabled={busy} className="rounded-none bg-rose-700 hover:bg-rose-800 uppercase text-xs tracking-wider font-semibold">
          {busy ? <><Loader2 size={13} className="mr-1 animate-spin" />Memproses…</> : <><ShieldAlert size={13} className="mr-1" />Buka Percakapan Audit</>}
        </Button>
      </section>

      {inspecting && (
        <section className="bg-white border-2 border-rose-400" data-testid="audit-inspecting">
          <div className="bg-rose-100 px-4 py-2 flex items-center justify-between">
            <div className="text-xs font-semibold text-rose-900 uppercase tracking-wider">AUDIT ACCESS · READ-ONLY</div>
            <button onClick={() => setInspecting(null)} className="text-xs text-rose-700 hover:text-rose-900">Tutup</button>
          </div>
          <div className="px-4 py-2 text-xs text-slate-700 border-b border-slate-200">
            <b>Peserta:</b> {(inspecting.participants || []).map((p) => `${p.name} (${p.division} Lv${p.level})`).join(" ↔ ")}
          </div>
          <div className="p-4 max-h-[500px] overflow-y-auto space-y-1.5 bg-slate-50">
            {(inspecting.messages || []).length === 0 && <div className="text-xs italic text-slate-500 text-center py-4">Belum ada pesan.</div>}
            {(inspecting.messages || []).map((m) => (
              <div key={m.id} className="text-xs border-l-2 border-slate-300 pl-2 bg-white p-2" data-testid={`audit-msg-${m.id}`}>
                <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                  <span><User size={9} className="inline mr-1" />{nameOf(m.sender_id)}</span>
                  <span><Clock size={9} className="inline mr-1" />{new Date(m.sent_at).toLocaleString("id-ID")}{m.edited_at && ` · diedit`}</span>
                </div>
                <div className="whitespace-pre-wrap text-slate-800">{m.body}</div>
                {(m.hidden_by || []).length > 0 && <div className="text-[9px] text-amber-700 mt-1 italic">Disembunyikan oleh: {(m.hidden_by || []).map(nameOf).join(", ")}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-white border border-slate-200" data-testid="audit-log-table">
        <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-2">
          <FileText size={14} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-[#0B1D3A]">Log Akses Audit</h3>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-3 py-2">Waktu</th>
              <th className="px-3 py-2">Auditor</th>
              <th className="px-3 py-2">Peserta Target</th>
              <th className="px-3 py-2">Alasan</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-400 italic">Belum ada akses audit.</td></tr>}
            {logs.map((l) => (
              <tr key={l.id} className="border-b border-slate-100">
                <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{new Date(l.accessed_at).toLocaleString("id-ID")}</td>
                <td className="px-3 py-1.5">{l.auditor_name}</td>
                <td className="px-3 py-1.5">{(l.target_participants || []).map(nameOf).join(" ↔ ")}</td>
                <td className="px-3 py-1.5 max-w-[360px] truncate" title={l.reason}>{l.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function UserPickField({ label, value, onChange, users, testid }) {
  const [q, setQ] = useState("");
  const shown = q.trim() ? users.filter((u) => (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase())).slice(0, 10) : [];
  const selected = users.find((u) => u.id === value);
  return (
    <div>
      <label className="overline text-slate-600 text-[10px]">{label}</label>
      {selected ? (
        <div className="border border-slate-300 px-2 py-1.5 text-xs flex items-center justify-between mt-0.5">
          <span>{selected.name} <span className="text-slate-500">· {selected.division} Lv{selected.level}</span></span>
          <button onClick={() => { onChange(""); setQ(""); }} className="text-rose-600 hover:text-rose-800 text-[10px] uppercase">Ganti</button>
        </div>
      ) : (
        <div>
          <div className="relative mt-0.5">
            <Search size={12} className="absolute left-2 top-2.5 text-slate-400" />
            <Input data-testid={testid} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama/email…" className="rounded-none pl-7 h-8 text-xs" />
          </div>
          {shown.length > 0 && (
            <ul className="mt-1 max-h-32 overflow-y-auto border border-slate-200 bg-white text-xs">
              {shown.map((u) => (
                <li key={u.id} onClick={() => { onChange(u.id); setQ(""); }} className="px-2 py-1 hover:bg-slate-50 cursor-pointer" data-testid={`${testid}-opt-${u.id}`}>
                  {u.name} <span className="text-slate-500">· {u.division} Lv{u.level}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
